import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router";

import { ApiHelper, USER_ROLE_ADMIN } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import DeleteForeverOutlined from "@mui/icons-material/DeleteForeverOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import { Box, Dialog, Grid, Stack, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import AgentAutoComplete, {
  dedupeBrokersByToken,
  normalizeAgentTokens,
} from "components/Marketing/AgentAutoComplete";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { formatNumber, formatProducerName } from "utility/formatters";

import { formatDate, formatDateContext, formatPrice } from "../../utility/formatters";
import DropdownSelector from "../DropdownSelector";
import { BrokerIcon } from "../Icons/Icons";
import { SalesEditDialog } from "../Marketing/SalesEditDialog";
import { formatDeliveryPeriod, SALE_CATEGORY_CASH, SALE_CATEGORY_HTA, saleForDuplicate } from "../Marketing/SalesHelper";
import { PrintableTilleyDataGrid } from "../shared/DataGrid/PrintableTilleyDataGrid";

export default function CashSalesWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });

  const [isLoadingCurrent, setIsLoadingCurrent] = useState(false);
  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const [editingSale, setEditingSale] = useState(null);
  const [showProducerSelect, setShowProducerSelect] = useState(false);
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  // ---

  const [brokers, setBrokers] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sales, setSales] = useState(null);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadBrokers = async () => {
    try {
      if (globalState?.agent_token) {
        const _brokersFilter = {
          agent_token: globalState?.agent_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
      } else if (globalState?.user_role === USER_ROLE_ADMIN) {
        const _brokersFilter = {};
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);

        setBrokers(_brokers);
      }
    } catch (err) {
      setBrokers(null);
      handleError(err);
    }
  };

  // --- --- ---

  // ---

  const _loadSales = async () => {
    try {
      setIsLoadingCurrent(true);

      const _filter = {
        agent_token: globalState?.agent_token,
        crop_year: currentYear,
      };

      const promises = (filter?.filter_agent_token || []).map(async (agentToken) => {
        if (filter?.filter_agent_token && filter?.filter_agent_token.length) {
          _filter.filter_agent_token = agentToken;
        }

        const apiClaims = {};
        if (globalState?.user_role !== USER_ROLE_ADMIN) {
          apiClaims.agent_token = globalState?.agent_token;
        }

        return await UsersApiHelper.users_search("Sale", _filter, apiClaims);
      });

      if (promises.length === 0) {
        setSales(null);
        setIsLoading(false);
        setIsLoadingCurrent(false);
        return;
      }

      const allResults = await Promise.all(promises);

      const distinctAllResults = _.uniqBy(allResults.flat(), (item) => `${item.id}`);

      // Add dummy fields:
      const _filteredSales = distinctAllResults.map((s) => {
        if (s?.category === SALE_CATEGORY_CASH || s?.category === SALE_CATEGORY_HTA) {
          s.price_cash = (s?.price || 0) + (s?.basis || 0);
        } else {
          s.price_cash = null;
        }
        return s;
      });

      setSales(distinctAllResults);
    } catch (err) {
      setSales(null);
      handleError(err);
    } finally {
      setIsLoading(false);
      setIsLoadingCurrent(false);
    }
  };

  const updateSale = async (sale) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("marketing/Sale", sale, globalState);
      _loadSales();
    } catch (err) {
      handleError(err);
    }
  };

  const closeSaleDialog = () => {
    setEditingSale(null);
    setShowProducerSelect(false);
  };

  const handleDuplicateCashSale = (cashSale) => {
    setShowProducerSelect(true);
    setEditingSale(saleForDuplicate(cashSale));
  };

  const handleDeleteCashSale = (cashSale) => {
    confirmDialogActions.confirm({
      title: "Delete this entry?",
      prompt: "Are you sure you want to delete this entry?",
      promptDetail: null,
      callback: async () => {
        await updateSale({
          ...cashSale,
          is_active: false,
        });
        closeSaleDialog();
      },
    });
  };

  // ---

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    filter_agent_token: normalizeAgentTokens(
      globalState?.filter_agent_token?.length
        ? globalState.filter_agent_token
        : [globalState?.agent_token].filter(Boolean)
    ),
    search_producer_name: globalState?.search_text_portfolio,
  });
  const selectedBroker = useMemo(() => {
    const tokens = new Set(normalizeAgentTokens(filter?.filter_agent_token));
    return dedupeBrokersByToken(
      brokers?.filter((broker) => tokens.has(String(broker?.agent_token ?? "").toLowerCase())) || []
    );
  }, [brokers, filter?.filter_agent_token, globalState?.agent_token]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: filter?.filter_agent_token,
      },
    });
  }, [filter?.filter_agent_token]);

  const handleChangeAgentToken = async (agentTokens) => {
    dispatchFilter({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: normalizeAgentTokens(agentTokens.map((item) => item.agent_token)),
      },
    });
  };

  const filterHandleChangeState = (event, value) => {
    value = value ?? event.target.value;

    if (value !== filter?.search_producer_name) {
      dispatchFilter({
        type: "update",
        payload: { key: event.target.name, value: value },
      });

      // Persist search text?
      if (event.target.name === "search_producer_name") {
        persistSearchText(event?.target?.value);
      }
    }
  };

  const persistSearchText = async (searchText) => {
    globalDispatch({
      type: "update",
      payload: {
        key: "search_text_portfolio",
        value: searchText,
      },
    });
  };

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  // ---

  useEffect(() => {
    if (globalState?.agent_token || globalState?.user_role === USER_ROLE_ADMIN) {
      loadBrokers();
    }
  }, [globalState?.agent_token, globalState?.user_role, filter]);

  useEffect(() => {
    if (currentYear && (filter?.filter_agent_token || globalState?.agent_token)) {
      _loadSales();
    }
  }, [currentYear, filter, globalState?.agent_token]);

  // --- --- ---

  const _formatProducerNameCallback = useCallback((row) => {
    return formatProducerName(row);
  }, []);

  // Memoize column definition
  const sales_columns = useMemo(
    () => [
      {
        field: "producer_token",
        headerName: "Producer",
        minWidth: 200,
        valueGetter: (value, row) => {
          return _formatProducerNameCallback(row);
        },
        flex: 1,
      },
      {
        field: "trade_date",
        headerName: "Trade Date",
        type: "date",
        width: 100,
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: "financial_institution_name",
        headerName: "Buyer",
        minWidth: 150,
        flex: 1,
        cellClassName: "wrap-text",
      },
      {
        field: "commodity_name",
        headerName: "Commodity",
        minWidth: 100,
        flex: 1,
      },
      {
        field: "category",
        headerName: "Category",
        width: 100,
      },
      {
        field: "symbol",
        headerName: "Symbol",
        width: 100,
      },
      {
        field: "price",
        headerName: "Futures",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatNumber(value, 4) : "0"),
      },
      {
        field: "basis",
        headerName: "Basis",
        type: "number",
        minWidth: 120,
        flex: 1,
        align: "right",
        valueFormatter: (value) => (value ? formatNumber(value, 4) : "0"),
      },
      {
        field: "price_cash",
        headerName: "Cash",
        minWidth: 120,
        flex: 1,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (value ? formatNumber(value, 4) : "0"),
      },
      {
        field: "amount",
        headerName: "Quantity",
        type: "number",
        width: 100,
        valueFormatter: (value) => formatNumber(value, 0),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "delivery_period_start",
        headerName: "Delivery",
        width: 100,
        valueFormatter: (value, row) =>
          sales?.length ? formatDeliveryPeriod(sales?.find((s) => s.id === row?.id)) : null,
      },
      {
        field: "contract_number",
        headerName: "Contract",
        minWidth: 120,
        flex: 1,
      },
      {
        field: "revenue",
        headerName: "Revenue",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
          <Box>
            <abbr title={formatDateContext(params?.row?.market_value_timestamp)}>{formatPrice(params.row?.revenue, 0)}</abbr>
          </Box>
        ),
        align: "right",
        headerAlign: "right",
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 168,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<ModeEditOutlineIcon />}
            label="Edit"
            onClick={() => {
              setShowProducerSelect(false);
              setEditingSale(params?.row);
            }}
          />,
          <GridActionsCellItem
            key="view-details"
            icon={<EastOutlinedIcon />}
            label="Details"
            onClick={() =>
              navigate(`/marketing/sales/${params?.row.producer_token}/${currentYear}/${params?.row?.commodity_name}`)
            }
          />,
          <GridActionsCellItem
            key="duplicate"
            icon={<ContentCopyOutlined />}
            label="Duplicate"
            onClick={() => handleDuplicateCashSale(params?.row)}
            showInMenu
            divider
            sx={{ color: "text.secondary" }}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteForeverOutlined color="error" />}
            label="Delete"
            onClick={() => handleDeleteCashSale(params?.row)}
            showInMenu
            sx={{ color: "error.main" }}
          />,
        ],
      },
    ],
    [sales]
  );

  const filteredSalesData = useMemo(
    () =>
      sales?.filter((row) => {
        if (filter?.search_producer_name) {
          return (
            row.last_name?.toLowerCase().includes(filter?.search_producer_name.toLowerCase()) ||
            row.first_name?.toLowerCase().includes(filter?.search_producer_name.toLowerCase()) ||
            row.business_name?.toLowerCase().includes(filter?.search_producer_name.toLowerCase()) ||
            row.producer_token?.toLowerCase().includes(filter?.search_producer_name.toLowerCase()) ||
            formatProducerName(row)?.toLowerCase().includes(filter?.search_producer_name.toLowerCase())
          );
        }
        return true;
      }),
    [sales, filter?.search_producer_name]
  );

  return (
    <>
      <Box className="landscape-section">
        <WorkspaceHeader
          title="Cash Sales"
          category={{
            title: "Broker",
            icon: <BrokerIcon />,
          }}
        />
        <Grid container>
          <Grid item xs={12}>
            <Stack
              className="headerWithMenuButton"
              sx={{ flexDirection: { xs: "column", md: "row" }, alignItems: "start", gap: 2 }}
            >
              <Box
                sx={{
                  width: { xs: "100%", md: "480px" },
                  "& .MuiFormControl-root": {
                    width: "100% !important",
                  },
                }}
              >
                <AgentAutoComplete
                  brokers={brokers}
                  selectedBroker={selectedBroker}
                  onChange={handleChangeAgentToken}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "start",
                  paddingTop: { md: "1.75rem" },
                  margin: { md: "0 !important" },
                }}
              >
                <DropdownSelector value={currentYear} onChange={setCurrentYear} options={getBudgetYears()} />
              </Box>

              {/* TODO: Add search icon */}
              <StatefulControlledTextField
                label="Search"
                name="search_producer_name"
                className="search-input"
                isRequired={false}
                defaultValue={filter?.search_producer_name || ""}
                onChange={filterHandleChangeState}
                InputLabelProps={{ shrink: true }}
                fullWidth={isMobile}
                sx={{ width: { xs: "70%", sm: "auto" } }}
              />

              {isLoading && (
                <Box sx={{ paddingTop: { md: "1.75rem" } }}>
                  <CircularProgress />
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>

        <PrintableTilleyDataGrid
          hideColumns={["actions"]}
          rows={filteredSalesData || []}
          columns={sales_columns}
          pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
          defaultPageSize={isMobile ? 5 : 10}
          gridKey="cash-sales-broker"
          getRowId={(row) => row.id}
          isLoading={isLoadingCurrent}
        />
        <Dialog open={Boolean(editingSale !== null)}>
          <SalesEditDialog
            producer_token={editingSale?.producer_token} //Override producer_token
            showProducerSelect={showProducerSelect}
            onChange={updateSale}
            onClose={closeSaleDialog}
            onDelete={handleDeleteCashSale}
            sale={editingSale}
            crop_year={currentYear}
          />
        </Dialog>
        {confirmDialogComponent}
      </Box>
    </>
  );
}
